import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button, Label, Input, Switch } from '@/components/ui';
import { type ThemeKnobs, clampRadius, MIN_RADIUS, MAX_RADIUS } from '@/lib/theme/theme-knobs';
import { ColorField } from './color-field';
import { EyeDropperButton } from './eyedropper-button';
import { useImageEyedropper } from './use-image-eyedropper';
import type { KnobsSource } from './use-stylesheet-knobs';

interface ThemeEditorProps {
  /** Current knob values (controlled). */
  knobs: ThemeKnobs;
  /** Emitted on every knob change. */
  onChange: (knobs: ThemeKnobs) => void;
  /** Service background image URL — drives the canvas pipette. */
  backgroundUrl?: string | null;
  /** Read-only mode for non-SERVICE_WRITE roles. */
  disabled?: boolean;
  /** Whether the current knobs differ from the loaded seed. */
  dirty?: boolean;
  /** Where the seed came from — drives the custom-CSS / load-error notices. */
  source?: KnobsSource;
  /** Reset back to the loaded seed. */
  onReset?: () => void;
  /** Persist the current knobs (Phase 4). Absent → the Save bar is hidden. */
  onSave?: () => void;
  /** Save in flight — disables Save and shows the saving label. */
  saving?: boolean;
}

/**
 * Theme editor knob panel (Spec 340). Controlled/presentational: it renders the
 * fixed v1 knob set (CTA fill, optional gradient, glow, radius) and emits the next
 * {@link ThemeKnobs} on change. Saving (Phase 4, Issue #341) is wired via `onSave`
 * and is only enabled when there are unsaved changes.
 */
export function ThemeEditor({
  knobs,
  onChange,
  backgroundUrl,
  disabled = false,
  dirty = false,
  source = 'default',
  onReset,
  onSave,
  saving = false,
}: ThemeEditorProps) {
  const { t } = useTranslation('service-theme');
  const pipette = useImageEyedropper(backgroundUrl);

  const update = (patch: Partial<ThemeKnobs>) => {
    if (disabled) return;
    onChange({ ...knobs, ...patch });
  };

  const handlePipetteClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (disabled || !pipette.enabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const rx = (event.clientX - rect.left) / rect.width;
    const ry = (event.clientY - rect.top) / rect.height;
    const hex = pipette.pickColorAtRatio(rx, ry);
    if (hex) update({ ctaColor: hex });
  };

  const gradientOn = knobs.ctaGradientTo !== null;

  return (
    <div className="flex flex-col gap-6">
      {source === 'custom' && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('warning.customCss')}</span>
        </div>
      )}
      {source === 'error' && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('warning.loadError')}</span>
        </div>
      )}

      {/* CTA fill */}
      <ColorField
        id="theme-cta-color"
        label={t('cta.label')}
        value={knobs.ctaColor}
        onChange={(hex) => update({ ctaColor: hex })}
        disabled={disabled}
        actions={
          <EyeDropperButton
            onPick={(hex) => update({ ctaColor: hex })}
            disabled={disabled}
            label={t('cta.eyedropper')}
          />
        }
      />

      {/* Image pipette — always shown; disabled with a hint when there is no
          background image or the canvas can't be sampled (CORS/taint). In read-only
          mode a loaded image is shown statically (no false "loading" state). */}
      <div className="flex flex-col gap-1">
        <Label>{t('pipette.label')}</Label>
        {!backgroundUrl ? (
          <p className="text-xs text-muted-foreground">{t('pipette.noBackground')}</p>
        ) : pipette.status === 'error' ? (
          <p className="text-xs text-muted-foreground">{t('pipette.unavailable')}</p>
        ) : pipette.status !== 'ready' ? (
          <p className="text-xs text-muted-foreground">{t('pipette.loading')}</p>
        ) : disabled ? (
          <img
            src={backgroundUrl}
            crossOrigin="anonymous"
            alt={t('pipette.alt')}
            className="h-20 w-full rounded-md border border-input object-cover opacity-70"
          />
        ) : (
          <img
            src={backgroundUrl}
            crossOrigin="anonymous"
            alt={t('pipette.alt')}
            onClick={handlePipetteClick}
            className="h-20 w-full cursor-crosshair rounded-md border border-input object-cover"
          />
        )}
      </div>

      {/* Gradient */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="theme-gradient-switch">{t('gradient.label')}</Label>
          <Switch
            id="theme-gradient-switch"
            checked={gradientOn}
            disabled={disabled}
            onCheckedChange={(checked) =>
              update({ ctaGradientTo: checked ? (knobs.ctaGradientTo ?? knobs.ctaColor) : null })
            }
          />
        </div>
        {gradientOn && (
          <ColorField
            id="theme-cta-gradient"
            label={t('gradient.toLabel')}
            value={knobs.ctaGradientTo ?? knobs.ctaColor}
            onChange={(hex) => update({ ctaGradientTo: hex })}
            disabled={disabled}
          />
        )}
      </div>

      {/* Glow */}
      <div className="flex items-center justify-between">
        <Label htmlFor="theme-glow-switch">{t('glow.label')}</Label>
        <Switch
          id="theme-glow-switch"
          checked={knobs.glow}
          disabled={disabled}
          onCheckedChange={(checked) => update({ glow: checked })}
        />
      </div>

      {/* Radius */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="theme-radius-number">{t('radius.label')}</Label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            aria-label={t('radius.label')}
            min={MIN_RADIUS}
            max={MAX_RADIUS}
            value={knobs.radius}
            disabled={disabled}
            onChange={(e) => update({ radius: clampRadius(Number(e.target.value)) })}
            className="h-2 flex-1 cursor-pointer"
          />
          <Input
            id="theme-radius-number"
            type="number"
            min={MIN_RADIUS}
            max={MAX_RADIUS}
            value={knobs.radius}
            disabled={disabled}
            onChange={(e) => update({ radius: clampRadius(Number(e.target.value)) })}
            className="w-20"
          />
        </div>
        {knobs.radius >= MAX_RADIUS && (
          <p className="text-xs text-muted-foreground">{t('radius.pill')}</p>
        )}
      </div>

      {/* Save bar */}
      {!disabled && onSave && (
        <div className="flex items-center justify-between border-t pt-4">
          <Button type="button" onClick={onSave} disabled={!dirty || saving}>
            {saving ? t('save.saving') : t('save.button')}
          </Button>
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-muted-foreground">{t('dirty')}</span>}
            <Button type="button" variant="outline" onClick={onReset} disabled={!dirty || saving}>
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('reset.button')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
